import { hasItems } from "@repo/utils";

import type {
  Amounts,
  Contact,
  CustomerMessage,
  DeliveryArrangement,
  DeliveryService,
  Item,
  Order,
  Planner,
  Service,
} from "./types";

export const mapApiResponseToOrders = (order: any, id: string): Order[] => {
  if (!order) {
    return [];
  }
  const { metaData, cartData, createdAt, status, buCode } = order;

  const {
    customerMeetingPoint,
    bookedServiceProposal,
    salesPriceData,
    drawings,
    customer,
    attachments,
    customMessages,
    messages,
  } = cartData;

  const currencyCode =
    metaData?.totalPrice?.Currency || cartData?.currency || "";

  const creationMethod = "";
  const amounts = mapAmounts(order);
  const ordersArray: Order[] = [
    {
      orderValue: metaData?.totalPrice?.priceInclTax ?? 0,
      orderType: cartData?.orderType,
      orderNo: id,
      orderNoSource: "",
      currencyCode,
      orderCreationDate: createdAt,
      quotationExpiryDate: metaData?.quotationExpiryDate || "",
      orderStatus: status,
      amounts,
      items: getItems(cartData?.items),
      deliveryArrangements: bookedServiceProposal?.hasDelivery
        ? getDeliveryArrangements(bookedServiceProposal?.bookedServices)
        : [],
      paymentPlans: [],
      planners: getAssociatedDrawings(drawings),
      providedServices: bookedServiceProposal?.hasProvidedService
        ? getProvidedServices(
            salesPriceData?.providedServices,
            bookedServiceProposal?.bookedServices
          )
        : [],
      buCode,
      buName: customerMeetingPoint?.name,
      creationMethod,
      deliveryContact: getDeliveryContact(customer),
      attachments,
      customMessages: getCustomerMessage(customMessages, messages),
      orderAlias: metaData?.alias,
    },
  ];

  return ordersArray;
};

const getItems = (cartItems?: any[]): Item[] => {
  if (!cartItems) {
    return [];
  }

  return cartItems?.map((cartItem: any) => {
    const { itemId, quantity, childItems, price, name, imageUrl, itemType } =
      cartItem || {};

    return {
      itemNo: itemId,
      itemType,
      quantity,
      childItems: getItems(childItems ?? []),
      salesPrice: {
        currency: price?.currencyCode || "",
        value: price?.priceExclTax ?? 0,
      },
      itemName: name || "",
      imageVariants: [
        {
          quality: "",
          height: 50,
          width: 50,
          href: imageUrl || childItems?.[0]?.imageUrl || "",
        },
      ],
      notes: [],
    };
  });
};

const getAssociatedDrawings = (drawings?: any[]): Planner[] => {
  if (!drawings) {
    return [];
  }
  const uniqueDrawingIds = new Set<string>();

  return drawings.reduce((result: Planner[], drawing: any) => {
    const drawingId = drawing?.drawingId;
    const drawingIdSource = drawing?.drawingIdSource;

    if (drawingId && !uniqueDrawingIds.has(drawingId)) {
      uniqueDrawingIds.add(drawingId);

      result.push({
        plannerId: drawingId,
        plannerIdSource: drawingIdSource || "",
      });
    }

    return result;
  }, []);
};

const mapTimeWindow = (timeWindow: any) => ({
  fromLocalDateTime: timeWindow?.fromDateTime || "",
  timeZone: timeWindow?.timeZone || "",
  toLocalDateTime: timeWindow?.toDateTime || "",
});

const mapService = (
  providedService?: any,
  bookedServiceData?: any
): Service => {
  return {
    price: {
      currencyCode:
        providedService?.servicePrice?.[0]?.inclSavings?.currencyCode || "",
      priceExclTax:
        providedService?.servicePrice?.[0]?.inclSavings?.priceExclTax || 0,
      priceInclTax:
        providedService?.servicePrice?.[0]?.inclSavings?.priceInclTax || 0,
    },
    referencedItems: [],
    serviceItemNo: providedService?.serviceItemNo || "",
    serviceItemType: providedService?.serviceItemType || "",
    serviceName: bookedServiceData?.serviceName || "",
    servicePaymentTo: bookedServiceData?.serviceProviderPreferredPayment || "",
    serviceProductId: providedService?.serviceProductId || "",
    serviceProviderId: bookedServiceData?.serviceProviderId || "",
    serviceProviderName: bookedServiceData?.serviceProviderName || "",
    status: "",
    statusText: "",
    taxRates: [],
    timeWindows: [mapTimeWindow(bookedServiceData?.timeWindows?.[0])],
  };
};

const getProvidedServices = (
  providedServices?: any[],
  bookedServices?: any
): Service[] => {
  if (!providedServices) {
    return [];
  }

  return providedServices.map((providedService: any) => {
    const bookedServiceData = bookedServices?.find(
      (bookedService: any) =>
        bookedService?.serviceProductId === providedService?.serviceProductId
    );

    return mapService(providedService, bookedServiceData);
  });
};

const getDeliveryArrangements = (
  deliveryArrangement: any[]
): DeliveryArrangement[] => {
  if (!Array.isArray(deliveryArrangement) || deliveryArrangement.length === 0) {
    return [];
  }

  const [deliveryItem = {}] = deliveryArrangement;

  const deliveryServices: DeliveryService[] = [
    {
      deliveryDestinationType: deliveryItem.customerServiceType,
      price: {
        currencyCode: deliveryItem.price?.currencyCode || "",
        priceExclTax: deliveryItem.price?.priceExclTax ?? 0,
        priceInclTax: deliveryItem.price?.priceInclTax ?? 0,
      },
      referencedItems: deliveryItem.referencedItems || [],
      serviceId: deliveryItem.serviceId || "",
      serviceItemNo: "",
      serviceItemType: "",
      serviceName: deliveryItem.serviceName || "",
      status: deliveryItem.status || "",
      statusText: deliveryItem.statusTranslated || "",
      taxRates: [],
      timeWindows: [],
    },
  ];

  const deliveryArrangements: DeliveryArrangement[] = [
    {
      deliveryServices,
      serviceSubType: deliveryItem.solutionReference || "",
      serviceType: deliveryItem.fulfillmentMethod || "",
    },
  ];

  return deliveryArrangements;
};

const mapAmounts = (orderData: any): Amounts => {
  const { metaData, cartData } = orderData || {};
  const { salesPriceData } = cartData || {};
  const currencyCode =
    metaData?.totalPrice?.Currency || cartData?.currency || "";

  return {
    DeliveryService: {
      currencyCode,
      grossAmount:
        cartData?.salesPriceData?.orderSubtotal?.[0]?.delivery?.inclSavings
          ?.priceInclTax ?? 0,
      netAmount:
        cartData?.salesPriceData?.orderSubtotal?.[0]?.delivery?.inclSavings
          ?.priceExclTax ?? 0,
      vatAmount:
        cartData?.salesPriceData?.orderSubtotal?.[0]?.delivery?.inclSavings
          ?.indicativeTax ?? 0,
    },
    Total: {
      currencyCode,
      grossAmount:
        salesPriceData?.orderSummary?.summary?.[0]?.inclSavings?.priceInclTax ??
        0,
      netAmount:
        salesPriceData?.orderSummary?.summary?.[0]?.inclSavings?.priceExclTax ??
        0,
      vatAmount:
        salesPriceData?.orderSummary?.summary?.[0]?.inclSavings
          ?.indicativeTax ?? 0,
    },
    GoodsValue: {
      currencyCode,
      grossAmount:
        cartData?.salesPriceData?.orderSubtotal?.[0]?.product?.inclSavings
          ?.priceInclTax ?? 0,
      netAmount:
        cartData?.salesPriceData?.orderSubtotal?.[0]?.product?.inclSavings
          ?.priceExclTax ?? 0,
      vatAmount:
        cartData?.salesPriceData?.orderSubtotal?.[0]?.product?.inclSavings
          ?.indicativeTax ?? 0,
    },
    Service: {
      currencyCode,
      grossAmount:
        cartData?.salesPriceData?.orderSubtotal?.[0]?.service?.inclSavings
          ?.priceInclTax ?? 0,
      netAmount:
        cartData?.salesPriceData?.orderSubtotal?.[0]?.service?.inclSavings
          ?.priceExclTax ?? 0,
      vatAmount:
        cartData?.salesPriceData?.orderSubtotal?.[0]?.service?.inclSavings
          ?.indicativeTax ?? 0,
    },
    ToPay: {
      currencyCode,
      grossAmount:
        salesPriceData?.orderSummary?.summary?.[0]?.inclSavings?.priceInclTax ??
        0,
      netAmount:
        salesPriceData?.orderSummary?.summary?.[0]?.inclSavings?.priceExclTax ??
        0,
      vatAmount:
        salesPriceData?.orderSummary?.summary?.[0]?.inclSavings
          ?.indicativeTax ?? 0,
    },
  };
};

const getDeliveryContact = (customer: any): Contact => {
  const {
    addressLine1,
    city,
    countryCode,
    zipCode,
    email,
    firstName,
    lastName,
    mobilePhone,
    preferredLocale,
    isBusinessCustomer,
  } = customer || {};

  return {
    address: {
      addressLine1: addressLine1 || "",
      cityName: city || "",
      countryCode: countryCode || "",
      streetName: "",
      zipCode: zipCode || "",
    },
    customerContactDetails: {
      locale: preferredLocale,
      preferredContactType: "",
      contactTypeValues: {
        EMAIL: email || "",
        MOBILE: mobilePhone || "",
      },
      firstName: firstName || "",
      lastName: lastName || "",
      customerType: isBusinessCustomer ? "Business" : "Individual",
      familyNumber: customer?.ikeaFamilyNumber || "",
      globalCustomerId: customer?.partyId || "",
    },
  };
};

const getCustomerMessage = (
  customMessages: CustomerMessage[] = [],
  messages: CustomerMessage[] = []
): CustomerMessage[] => {
  const source = hasItems(customMessages)
    ? customMessages
    : hasItems(messages)
      ? messages
      : [];

  return source.map(({ messageText, messageType }) => ({
    messageText,
    messageType,
  }));
};
